import table from './patient_document_labels.json'

// Fixed, research-based labels for the patient documents (visit summary / discharge instructions) in each local
// language: { english: { pcm | yo | ha | ig: label } }, placeholders in braces ({name}, {date}, …). Used on the
// translated page instead of English or machine-translated labels; names, numbers and dates stay as they are.
//
// The JSON is identical to access-emr-core's config/patient_document_labels.json (which the Translator uses for the
// headings inside the translated instructions); both repos pin its hash in a test. It is built from the translation
// review (tmp-scribe-experiment/translation-review/document-labels.json; Yoruba in the respectful ẹ / yín) and is
// still subject to the native-speaker review gate (PATIENT_SUMMARY_LANGUAGES). Pure: no API/axios imports.

export const DOCUMENT_LABEL_LANGUAGES = ['pcm', 'yo', 'ha', 'ig'] as const
export type DocumentLabelLanguage = (typeof DOCUMENT_LABEL_LANGUAGES)[number]

const LABELS: Record<string, Record<DocumentLabelLanguage, string>> = table

type Values = Record<string, string | number>

const fill = (template: string, values: Values = {}): string =>
  template.replace(/\{(\w+)\}/g, (placeholder, key: string) =>
    values[key] !== undefined ? String(values[key]) : placeholder
  )

/** The language code when it has fixed labels, else null. */
export const documentLabelLanguage = (code: string | null | undefined): DocumentLabelLanguage | null =>
  (DOCUMENT_LABEL_LANGUAGES as readonly string[]).includes(code ?? '') ? (code as DocumentLabelLanguage) : null

/** The fixed label in `language` for an English label (falls back to the English when the table lacks it). */
export const documentLabel = (language: DocumentLabelLanguage, english: string, values?: Values): string =>
  fill(LABELS[english]?.[language] ?? english, values)

/** The English label itself, with its placeholders filled — the English page's wording. */
export const englishLabel = (english: string, values?: Values): string => fill(english, values)
